import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { FaChartLine, FaFilter, FaArrowUp, FaIndustry, FaGraduationCap, FaRocket, FaSearch } from 'react-icons/fa';
import { HiLightningBolt, HiSparkles } from 'react-icons/hi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const domains = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Marketing',
  'Energy',
  'Transportation'
];

const domainIcons = {
  Technology: FaRocket,
  Healthcare: FaIndustry,
  Finance: FaChartLine,
  Education: FaGraduationCap,
  Manufacturing: FaIndustry,
  Marketing: FaArrowUp,
  Energy: HiLightningBolt,
  Transportation: FaRocket
};

const mockData = {
  
    "Technology": {
      "Artificial Intelligence": [30, 45, 65, 85, 95, 100],
      "Generative AI / LLMs": [15, 25, 40, 60, 80, 100],
      "Cloud Computing & Cloud-Native": [40, 55, 70, 85, 90, 95],
      "Cybersecurity & DevSecOps": [35, 50, 65, 80, 85, 95],
      "Edge Computing / IoT Integration": [25, 35, 50, 65, 80, 90],
      "DevOps & Infrastructure as Code": [30, 45, 60, 75, 85, 90],
      "Data Science & Big Data Analytics": [25, 40, 60, 75, 85, 90],
      "Blockchain & DApp Development": [20, 35, 50, 65, 80, 85],
      "Quantum Computing": [15, 25, 40, 55, 70, 80],
      "Immersive Tech (AR/VR/XR)": [10, 25, 40, 55, 70, 80]
    },
    "Healthcare": {
      "Medical AI & Health Analytics": [10, 25, 40, 55, 70, 85],
      "Telemedicine / Virtual Health": [15, 30, 45, 60, 75, 85],
      "Digital Health / Healthcare IoT": [20, 35, 50, 65, 80, 90],
      "Genomics & Precision Medicine": [20, 35, 50, 65, 80, 85],
      "Medical Robotics": [10, 25, 40, 55, 70, 80],
      "Health Data Science": [25, 40, 55, 70, 85, 90]
    },
    "Finance": {
      "FinTech / Digital Banking": [25, 40, 55, 70, 85, 90],
      "Risk Analytics & AI in Trading": [15, 30, 45, 60, 75, 85],
      "Blockchain Finance / DeFi": [20, 35, 50, 65, 80, 85],
      "Cryptocurrency Tech": [20, 35, 50, 65, 80, 85],
      "RegTech / Compliance Automation": [15, 30, 45, 60, 75, 85],
      "Financial Data Science": [25, 40, 55, 70, 85, 90]
    },
    "Education": {
      "EdTech Development / Digital Learning": [25, 40, 55, 70, 85, 90],
      "Learning Analytics & Adaptive Learning": [20, 35, 50, 65, 80, 90],
      "AI in Education / Prompting Tools": [15, 30, 45, 60, 75, 90],
      "VR / Immersive Learning": [10, 25, 40, 55, 70, 80],
      "Digital Assessment & Experience Design": [25, 40, 55, 70, 85, 90]
    },
    "Manufacturing": {
      "Industrial IoT & Smart Manufacturing": [20, 35, 50, 65, 80, 90],
      "Predictive Maintenance & Digital Twins": [20, 35, 50, 65, 80, 90],
      "Robotic Process Automation": [15, 30, 45, 60, 75, 85],
      "Supply Chain Analytics": [25, 40, 55, 70, 85, 90],
      "Quality Control AI": [20, 35, 50, 65, 80, 85],
      "Sustainability Tech (Green Manufacturing)": [15, 30, 45, 60, 75, 90]
    },
    "Marketing": {
      "Digital & Content Marketing": [30, 45, 60, 75, 85, 90],
      "Social Media Marketing & Automation": [35, 50, 65, 80, 90, 95],
      "Marketing Analytics & Data Science": [25, 40, 55, 70, 85, 90],
      "AI in Marketing / GenAI Content Creation": [15, 30, 45, 60, 75, 90],
      "SEO / SEM & Customer Engagement": [30, 45, 60, 75, 85, 90],
      "Creativity & Innovation, Prompt Design": [25, 40, 60, 80, 95, 100]
    },
    "Energy": {
      "Renewable Energy Tech & Clean Energy": [25, 40, 55, 70, 85, 95],
      "Smart Grid & Energy IoT": [20, 35, 50, 65, 80, 90],
      "Energy Analytics & Storage Tech": [15, 30, 45, 60, 75, 85],
      "Carbon Capture & ESG Monitoring": [15, 30, 45, 60, 75, 90],
      "Energy Management Systems": [20, 35, 50, 65, 80, 90]
    },
    "Transportation": {
      "Autonomous & Electric Vehicle Tech": [15, 30, 45, 60, 75, 90],
      "Smart Transportation & Logistics Optimization": [20, 35, 50, 65, 80, 90],
      "Fleet Management & Mobility Analytics": [25, 40, 55, 70, 85, 90],
      "Transportation IoT & Smart City Mobility": [20, 35, 50, 65, 80, 90]
    }
  
  
};

const years = ['2021', '2022', '2023', '2024', '2025','2026'];

const TrendingSkills = () => {
  const [selectedDomain, setSelectedDomain] = useState('Technology');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (selectedDomain && mockData[selectedDomain]) {
      const skills = Object.keys(mockData[selectedDomain]);
      setSelectedSkills(skills);
      updateChartData(skills);
    }
  }, [selectedDomain]);

  const updateChartData = (skills) => {
    if (!skills || !selectedDomain || !mockData[selectedDomain]) return;

    const datasets = skills.map((skill, index) => ({
      label: skill,
      data: mockData[selectedDomain][skill],
      borderColor: `hsl(${index * 360 / skills.length}, 70%, 50%)`,
      backgroundColor: `hsla(${index * 360 / skills.length}, 70%, 50%, 0.1)`,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6
    }));

    setChartData({
      labels: years,
      datasets,
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          padding: 20,
          font: {
            size: window.innerWidth < 768 ? 10 : 11,
            family: "'Inter', sans-serif"
          },
          color: '#4B5563'
        }
      },
      title: {
        display: true,
        text: `Trending Skills in ${selectedDomain} (2020-2026)`,
        font: {
          size: window.innerWidth < 768 ? 14 : 16,
          weight: 'bold',
           family: "'Inter', sans-serif"
        },
        padding: {
          top: 10,
          bottom: 25
        },
        color: '#111827'
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#111827',
        bodyColor: '#4B5563',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: 12,
        boxPadding: 4,
        usePointStyle: true,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
             color: '#f3f4f6'
        },
        title: {
          display: true,
          text: 'Trend Score (0-100)',
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
            weight: 'bold'
          },
          color: '#6B7280'
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10
          },
          color: '#6B7280'
        }
      },
      x: {
        grid: {
            display: false
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10
          },
          color: '#6B7280'
        }
      }
    },
    interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
    }
  };

  const calculateGrowth = (skill) => {
    if (!selectedDomain || !mockData[selectedDomain] || !mockData[selectedDomain][skill]) {
      return 0;
    }
    const skillData = mockData[selectedDomain][skill];
    return skillData[4] - skillData[0];
  };

  const getGrowthColor = (growth) => {
    if (growth >= 50) return 'text-green-600';
    if (growth >= 30) return 'text-blue-600';
    if (growth >= 15) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const DomainIcon = domainIcons[selectedDomain] || FaChartLine;

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
      `
    }}>
      {/* Top Decoration */}
      <div className="h-[40rem] bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
         <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full text-sm font-medium mb-6 shadow-lg">
            <FaChartLine className="w-4 h-4 mr-2" />
            Market Intelligence
          </div>
          <h1 className="heading-primary mb-6 text-white drop-shadow-sm">
            Trending Skills <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">Insights</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Stay ahead of the curve with real-time insights into the most in-demand skills across industries
          </p>
        </div>
      
        {/* Domain Filter */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 mb-8 border border-white/50 relative overflow-hidden">
           <div className="absolute top-0 right-0 w-32 h-32 bg-primary-100 rounded-bl-full opacity-50 z-0"></div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary-500/30">
                     <FaFilter className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Select Industry Domain</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {domains.map((domain) => {
                const Icon = domainIcons[domain] || FaChartLine;
                const isSelected = selectedDomain === domain;
                return (
                    <button
                    key={domain}
                    onClick={() => setSelectedDomain(domain)}
                    className={`
                        relative overflow-hidden p-4 rounded-xl border-2 transition-all duration-300 group
                        ${isSelected 
                        ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-lg scale-105' 
                        : 'border-transparent bg-white/50 hover:bg-white hover:border-primary-200 text-gray-600 shadow-sm hover:shadow-md'
                        }
                    `}
                    >
                    <div className="relative z-10 flex flex-col items-center">
                        <Icon className={`w-6 h-6 mb-2 transition-transform duration-300 ${isSelected ? 'scale-110' : 'group-hover:scale-110 text-gray-400 group-hover:text-primary-500'}`} />
                        <span className="text-xs font-bold leading-tight text-center">{domain}</span>
                    </div>
                    {isSelected && (
                         <div className="absolute inset-0 bg-gradient-to-br from-primary-100/50 to-transparent pointer-events-none"></div>
                    )}
                    </button>
                );
                })}
            </div>
          </div>
        </div>

        {/* Chart Container */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 mb-8 border border-white/50">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-pink-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-secondary-500/30">
                <DomainIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedDomain} Trends</h2>
                <p className="text-sm text-gray-500 font-medium">Skill demand over the last half-decade</p>
              </div>
            </div>
          </div>
          <div className="h-[400px] md:h-[500px] w-full bg-white/50 rounded-2xl p-4 border border-gray-100 shadow-inner">
            {chartData && (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Skills List */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/30">
                 <HiSparkles className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Top Skills in {selectedDomain}
              </h2>
            </div>
            <div className="px-3 py-1 bg-gray-100 rounded-full text-xs font-bold text-gray-500 uppercase tracking-wide">
              {selectedSkills.length} skills analyzed
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedSkills.map((skill, index) => {
              const growth = calculateGrowth(skill);
              return (
                <Link
                  key={skill}
                  to={`/trending-skills/${encodeURIComponent(selectedDomain)}/${encodeURIComponent(skill)}`}
                  className="group relative block p-5 bg-white border border-gray-100 rounded-2xl transition-all duration-300 hover:shadow-xl hover:border-primary-200 hover:-translate-y-1 overflow-hidden"
                >
                   {/* Hover Gradient Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="relative z-10">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-gray-900 group-hover:text-primary-700 transition-colors text-lg">
                          {skill}
                        </h3>
                        <span className={`flex-shrink-0 ml-2 text-xs font-bold px-2.5 py-1 rounded-lg ${
                            growth >= 50 ? 'bg-green-100 text-green-700' :
                            growth >= 30 ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                        }`}>
                          +{growth}%
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wide">
                          <span>Growth Rate</span>
                          <span className={`${getGrowthColor(growth)}`}>
                            {growth > 0 ? 'High Demand' : 'Steady'}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ease-out group-hover:scale-x-105 origin-left ${
                              growth >= 50 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                              growth >= 30 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                              growth >= 15 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                              'bg-gray-400'
                            }`}
                            style={{ width: `${Math.min((growth / 80) * 100, 100)}%` }}
                          ></div>
                        </div>
                         <div className="pt-2 flex items-center text-primary-600 text-sm font-bold opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                             View Details <FaChartLine className="ml-2 w-3 h-3" />
                         </div>
                      </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Insights Section */}
        <div className="grid md:grid-cols-3 gap-6 mt-8">
             {/* Highest Growth */}
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all group">
                <div className="flex items-center space-x-4 mb-4">
                     <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                         <FaArrowUp className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-gray-900">Highest Growth</h4>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mb-1">
                   {selectedSkills.reduce((max, skill) => {
                      const growth = calculateGrowth(skill);
                      return growth > max.growth ? { skill, growth } : max;
                    }, { skill: '', growth: 0 }).skill}
                </div>
                <p className="text-sm text-gray-500 font-medium">Leading the market right now</p>
            </div>
            
             {/* Emerging Skills */}
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all group">
                <div className="flex items-center space-x-4 mb-4">
                     <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                         <HiLightningBolt className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-gray-900">Emerging Skills</h4>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mb-1">
                    {selectedSkills.filter(skill => calculateGrowth(skill) >= 40).length}
                </div>
                <p className="text-sm text-gray-500 font-medium">Skills with 40%+ growth rate</p>
            </div>

            {/* Average Demand */}
             <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all group">
                <div className="flex items-center space-x-4 mb-4">
                     <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                         <FaRocket className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-gray-900">Market Demand</h4>
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mb-1">
                     {Math.round(selectedSkills.reduce((sum, skill) => sum + calculateGrowth(skill), 0) / selectedSkills.length)}%
                </div>
                <p className="text-sm text-gray-500 font-medium">Average growth across sector</p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default TrendingSkills;